import React from 'react';

import { LoadableAfterPaint } from '@confluence/loadable';
import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { fg } from '@confluence/feature-gating';

import { NotFound } from '../NotFound.tsx';

const ConfluenceMsTeamsSignInConfirmationComponent = LoadableAfterPaint({
	loader: async () =>
		import(
			/* webpackChunkName: "loadable-ConfluenceMsTeamsSignInConfirmationComponent" */ './ConfluenceMsTeamsSignInConfirmationComponent.tsx'
		),
});

/**
 * Native replacement for the Connect `generalPages` module `msteams-confluence` previously served
 * at `/wiki/plugins/servlet/ac/{addonKey}/sign-in-confirmation`.
 *
 * Reached from a Microsoft Teams sign-in popup rather than in-product navigation, so — unlike
 * `AdminConfluenceChatsIntegrationRoute` — there is no existing native page to redirect to when the
 * gate is off: `msteams-confluence`'s own `AuthEnd` decides which URL to send the user to in the
 * first place, and only does so once this gate is on for them. `NotFound` here is a safety net for
 * anyone hitting the URL directly before that.
 */
export const ConfluenceMsTeamsSignInConfirmationRoute: {
	(match: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS(): boolean;
} = ({ query }: RouteMatch): React.JSX.Element => {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (!fg('confluence-msteams-signin-confirmation-migration')) {
		return <NotFound />;
	}

	const successValue = typeof query.successValue === 'string' ? query.successValue : undefined;
	const baseUrl = typeof query.baseUrl === 'string' ? query.baseUrl : undefined;

	return (
		<ConfluenceMsTeamsSignInConfirmationComponent successValue={successValue} baseUrl={baseUrl} />
	);
};

// Shown inside a small sign-in popup, not the main Confluence chrome — no nav/breadcrumb to set,
// matching the pattern other chromeless routes (e.g. `ActionRedirectLegacyRoute`) use.
ConfluenceMsTeamsSignInConfirmationRoute.NAVIGATION_PARAMS = (): boolean => false;
